import Mark from 'mark.js/dist/mark.js';
import { getCssSelector } from 'css-selector-generator';
import hexToRgba from '_shared_/js/utils/hexToRgba.js';
import { collectEpubNoteMarkerAnchors } from './collectEpubNoteMarkerAnchors.js';
import { readFrameDocument } from './frameAccess.js';
import * as Sentry from '@sentry/browser';
import { isAndroid } from '../../../../utils/platform.js';
import { sendBreadcrumb } from './sentryBreadcrumb.js';
import {
    countTextBefore,
    findEnclosingElement,
    findRangeParentElements,
    normalizeWhitespace,
    readRangeText,
} from './rangeGeometry.js';

/**
 * HighlightManager - Manages all highlight-related functionality for EPUB content
 *
 * @class HighlightManager
 */
/**
 * Locates a highlight's first character inside the element Mark.js will count over.
 *
 * `startElementOffset` is the offset persisted at capture time, measured from the start of the
 * element the selection began in — the only anchor that survives a start element holding more
 * than one text node, where `startCss` alone resolves to the element's first text node. Falls
 * back to the reconstructed range for highlights saved before that field existed.
 *
 * @param {Element} markContext - Element whose text Mark.js counts over
 * @param {Element} startElement - Element the selection starts in
 * @param {number|undefined} startElementOffset - Characters before the selection within startElement
 * @param {Range} range - The reconstructed selection range
 * @returns {number} Characters before the highlight within markContext
 */
function measureMarkStartOffset(markContext, startElement, startElementOffset, range) {
    if (!Number.isFinite(startElementOffset)) {
        return countTextBefore(markContext, range.startContainer, range.startOffset);
    }
    return countTextBefore(markContext, startElement) + startElementOffset;
}

/**
 * Measures how many characters Mark.js must wrap: the end boundary counted the same way as the
 * start, so neither the text Mark.js skips nor the part of the start element before the
 * selection is in the length. The end is rebuilt from the end element's first text node, so an
 * end element holding several text nodes can put it before the start; the rebuilt range's own
 * length is the measure left in that case, until an end offset is persisted too.
 * @param {Element} markContext - Element whose text Mark.js counts over
 * @param {number} markStartOffset - Characters before the highlight within markContext
 * @param {Range} range - The reconstructed selection range
 * @returns {number}
 */
function measureMarkLength(markContext, markStartOffset, range) {
    const markEndOffset = countTextBefore(markContext, range.endContainer, range.endOffset);
    const markLength = markEndOffset - markStartOffset;
    return markLength > 0 ? markLength : range.toString().length;
}

export class HighlightManager {
    constructor({
        onHighlightCreated = () => {},
        onHighlightUpdated = () => {},
        onHighlightDeleted = () => {},
        onSearchHighlightRendered = () => {},
        onTrackEvent = () => {},
        onSetCurrentHighlight = () => {},
        onFulfillQueryWordsHighlight = () => {},
        onHandleAnonymousSessionSelections = () => {},
        onSelectHighlight = () => {},
        onSelectionInProgress = () => {}, // @R2 selection popup
        onClearSelection = () => {}, // @R2 dismiss selection popup
        onMarkersRendered = () => {},
    } = {}) {
        this.currentChapterHighlights = {};
        this.initHighlightsRetryCount = 0;
        this.markerSearch = null;

        this.onHighlightCreated = onHighlightCreated;
        this.onHighlightUpdated = onHighlightUpdated;
        this.onHighlightDeleted = onHighlightDeleted;
        this.onSearchHighlightRendered = onSearchHighlightRendered;
        this.onTrackEvent = onTrackEvent;
        this.onSetCurrentHighlight = onSetCurrentHighlight;
        this.onFulfillQueryWordsHighlight = onFulfillQueryWordsHighlight;
        this.onHandleAnonymousSessionSelections = onHandleAnonymousSessionSelections;
        this.onSelectHighlight = onSelectHighlight;
        this.onSelectionInProgress = onSelectionInProgress; // @R2
        this.onClearSelection = onClearSelection; // @R2
        this.onMarkersRendered = onMarkersRendered;
    }

    /**
     * Initialize highlights for the current chapter.
     * Uses a getter function to access current Vuex state on each retry,
     * avoiding the stale-closure bug where primitive values were captured by value.
     *
     * @param {Function} getSelectionsState - Returns { fetched: boolean, selections: Array }
     * @param {Array} context - Chapter context items with bodyDomNode and epubChapter
     */
    initHighlights(getSelectionsState, context) {
        const RETRY_INTERVAL_MS = 50;
        const MAX_RETRIES = 50;

        this.context = context;

        const { fetched, selections } = getSelectionsState();
        if (fetched) {
            this.updateCurrentChapterHighlights(selections, context);
            this.renderHighlights();
        } else if (this.initHighlightsRetryCount < MAX_RETRIES) {
            setTimeout(() => {
                this.initHighlightsRetryCount++;
                this.initHighlights(getSelectionsState, context);
            }, RETRY_INTERVAL_MS);
        } else {
            sendBreadcrumb(
                'selection',
                'highlight_render_failed',
                {
                    chapterHref: this.currentChapterHref,
                    retries: this.initHighlightsRetryCount,
                },
                'warning'
            );
        }
    }

    /**
     * Normalize a chapter reference for comparison.
     * R1 stored epubChapter as spineItem.idref (e.g., "book2"),
     * while R2 uses currentLocation.href (e.g., "book2.xhtml" or "OEBPS/book2.xhtml").
     * Strips path and extension so both formats match.
     *
     * @param {string} ref - Chapter reference (idref or href)
     * @returns {string} Normalized reference without path or extension
     */
    normalizeChapterRef(ref) {
        if (!ref) return '';
        // "OEBPS/book2.xhtml" → "book2", "book2" → "book2"
        return ref.replace(/^.*\//, '').replace(/\.[^.]+$/, '');
    }

    updateCurrentChapterHighlights(selections, context) {
        this.currentChapterHighlights = {};

        // @R2 Build fresh context from Readium's current content frames (_cframes)
        // instead of using the accumulated Vuex context array, which may contain
        // references to destroyed DOM nodes from previous chapter navigations.
        let contextToUse = context;
        if (this.getContentFrames) {
            const cframes = this.getContentFrames();
            if (cframes.length > 0) {
                const freshContext = cframes
                    .map(cframe => {
                        try {
                            const doc = readFrameDocument(cframe);
                            const body = doc?.body;
                            // R2 injects <base data-readium href="..."> with the chapter href
                            const baseEl = doc?.querySelector('base[data-readium]');
                            const fullHref = baseEl?.getAttribute('href') || '';

                            return {
                                bodyDomNode: body,
                                epubChapter: fullHref,
                            };
                        } catch {
                            return null;
                        }
                    })
                    .filter(c => c && c.bodyDomNode);

                if (freshContext.length > 0) {
                    contextToUse = freshContext;
                }
            }
        }

        contextToUse.map(contextItem => {
            const contextChapterNorm = this.normalizeChapterRef(contextItem.epubChapter);
            const contextBodyId = contextItem.bodyDomNode?.id || '';
            selections
                .filter(highlight => {
                    const highlightChapterNorm = this.normalizeChapterRef(highlight.epubChapter);

                    // 1. Direct normalized comparison (handles R1 "book2" vs R2 "book2.xhtml")
                    if (highlightChapterNorm === contextChapterNorm) {
                        return true;
                    }

                    // 2. Body DOM id fallback
                    if (highlight.epubChapter === contextBodyId) {
                        return true;
                    }

                    // 3. @R2 idref→href resolution: R1 stored OPF item id (e.g. "book2")
                    //    which maps to an href (e.g. "GeographyofBli_body_split_002.html")
                    //    via the content.opf manifest.
                    if (this.idrefToHrefMap) {
                        const resolvedHref = this.idrefToHrefMap.get(highlight.epubChapter);
                        if (resolvedHref) {
                            const resolvedNorm = this.normalizeChapterRef(resolvedHref);
                            if (resolvedNorm === contextChapterNorm) {
                                return true;
                            }
                        }
                    }

                    return false;
                })
                .map(highlight => {
                    highlight.isRendered = false;
                    highlight.contextBodyDomNode = contextItem.bodyDomNode;
                    highlight.epubChapter = contextItem.epubChapter;
                    this.currentChapterHighlights[highlight.uuid] = highlight;
                });
        });
    }

    renderHighlights() {
        const highlights = Object.values(this.currentChapterHighlights);
        if (highlights.length === 0) {
            // @R2 Even without highlights, setup selection listeners for new highlight creation
            this._setupSelectionListeners();
            this.onMarkersRendered();
            return;
        }

        // @R2 Content readiness check: R2 may fire frameLoaded with a placeholder body
        // (e.g. 16 chars) while the real decrypted content lives in a different iframe.
        // When the cached body isn't ready, scan all viewport iframes to find the real one.
        if (!this._isBodyContentReady(highlights)) {
            const realBody = this._findContentBody(highlights);

            if (realBody) {
                highlights.forEach(h => {
                    h.contextBodyDomNode = realBody;
                });
            } else {
                if (!this._bodyReadyRetryCount) this._bodyReadyRetryCount = 0;
                if (this._bodyReadyRetryCount < 20) {
                    this._bodyReadyRetryCount++;
                    setTimeout(() => this.renderHighlights(), 500);
                    return;
                }
                this._bodyReadyRetryCount = 0;
                return;
            }
        }
        this._bodyReadyRetryCount = 0;

        highlights.forEach(highlight => this.renderHighlight(highlight));

        // @R2 After initial render, watch for the cframe body (the ACTUAL visible frame)
        // to get real content. The initial render may be on a fallback viewport iframe
        // which is not the primary display — marks appear but mispositioned.
        // When the cframe body is ready, re-render there for correct positioning.
        this._watchForCframeRerender();

        // @R2 Setup text selection detection on content frames
        this._setupSelectionListeners();

        this.onMarkersRendered();
    }

    /**
     * @R2 Watch for R2's content frame body (_cframes) to get real content,
     * then re-render highlights there for correct visual positioning.
     *
     * R2's _cframes are the actual visible rendering frames, but they may start
     * with placeholder content (~16 chars) while decryption completes. When the
     * cframe body gets real content, re-render highlights there and clear marks
     * from the old (fallback) body.
     */
    _watchForCframeRerender() {
        if (this._cframeWatcherId) {
            clearInterval(this._cframeWatcherId);
            this._cframeWatcherId = null;
        }

        if (!this.getContentFrames) return;

        const highlights = Object.values(this.currentChapterHighlights);
        if (highlights.length === 0) return;

        const currentBody = highlights[0].contextBodyDomNode;
        const sampleText = this._getSampleTextForValidation(highlights);

        // Check if we're already on a cframe body — no need to watch
        const cframesNow = this.getContentFrames() || [];
        for (const cframe of cframesNow) {
            try {
                if (readFrameDocument(cframe)?.body === currentBody) {
                    return;
                }
            } catch {}
        }

        let attempts = 0;
        this._cframeWatcherId = setInterval(() => {
            attempts++;
            if (attempts > 30) {
                // 15 seconds
                clearInterval(this._cframeWatcherId);
                this._cframeWatcherId = null;
                return;
            }

            const cframes = this.getContentFrames() || [];
            for (const cframe of cframes) {
                try {
                    const body = readFrameDocument(cframe)?.body;
                    const textLen = body?.textContent?.length || 0;

                    if (textLen > 100 && this._bodyContainsSampleText(body, sampleText)) {
                        sendBreadcrumb('frame', 'cframe_rerender_detected', {
                            chapterHref: this.currentChapterHref,
                        });
                        // Unmark old body
                        const oldHighlights = Object.values(this.currentChapterHighlights);
                        for (const h of oldHighlights) {
                            try {
                                const oldMarker = new Mark(h.contextBodyDomNode);
                                oldMarker.unmark({ className: h.getCssClassForMarker() });
                            } catch {}
                        }

                        // Re-render on cframe body
                        for (const h of oldHighlights) {
                            h.isRendered = false;
                            h.contextBodyDomNode = body;
                        }
                        oldHighlights.forEach(h => this.renderHighlight(h));

                        // @R2 Re-setup selection listeners on the new cframe
                        this._setupSelectionListeners();

                        this.onMarkersRendered();

                        clearInterval(this._cframeWatcherId);
                        this._cframeWatcherId = null;
                        return;
                    }
                } catch {}
            }
        }, 500);
    }

    /**
     * @R2 Setup text selection detection on content frames.
     * Adds pointerup listeners to detect text selection since R2's native
     * textSelected event may not fire reliably in all iframe configurations.
     * Calls onSelectionInProgress (same as R2's textSelected flow) to show the popup.
     */
    _setupSelectionListeners() {
        // Clean up previous listeners
        if (this._selectionCleanups) {
            this._selectionCleanups.forEach(fn => fn());
            this._selectionCleanups = null;
        }

        const cleanups = [];
        const bodiesWithListeners = new Set();

        // Collect all frames that might contain visible content
        const frameDocs = [];

        // 1. cframes (primary content frames the user sees)
        if (this.getContentFrames) {
            const cframes = this.getContentFrames() || [];
            for (const cframe of cframes) {
                try {
                    const doc = readFrameDocument(cframe);
                    const body = doc?.body;
                    if (doc && body && body.textContent?.length > 50 && !bodiesWithListeners.has(body)) {
                        frameDocs.push({ doc, frameElement: doc.defaultView?.frameElement });
                        bodiesWithListeners.add(body);
                    }
                } catch {}
            }
        }

        // 2. Viewport iframes (fallback)
        const allBodies = this._collectAllIframeBodies();
        for (const { body } of allBodies) {
            try {
                if (body.textContent?.length > 50 && !bodiesWithListeners.has(body)) {
                    const doc = body.ownerDocument;
                    const frameElement = doc.defaultView?.frameElement;
                    if (doc && frameElement) {
                        frameDocs.push({ doc, frameElement });
                        bodiesWithListeners.add(body);
                    }
                }
            } catch {}
        }

        const POINTERUP_SETTLE_MS = 10;
        const SELECTIONCHANGE_CLEAR_MS = 50;
        const ANDROID_SELECTION_SETTLE_MS = 250;
        const onAndroid = isAndroid();

        // anchorNode/range nodes can be Text nodes — resolve to the nearest element
        // before calling .closest() / inspecting attributes.
        const isInsideMark = node => !!findEnclosingElement(node)?.closest?.('mark');

        for (const { doc, frameElement } of frameDocs) {
            const emitCurrentSelection = () => {
                const sel = doc.defaultView?.getSelection();
                if (!sel?.toString().trim()) return false;

                const range = sel.getRangeAt(0);
                const text = readRangeText(range);
                const rects = range.getClientRects();
                if (!rects || rects.length === 0) return false;

                // Tapping a `<mark>` auto-selects its text on Android; the
                // highlight's own click handler owns that flow.
                if (isInsideMark(range.commonAncestorContainer)) return false;

                this.onSelectionInProgress({
                    text,
                    position: { x: rects[0].x, y: rects[0].y, width: rects[0].width, height: rects[0].height },
                    iframeDocument: frameElement,
                    range,
                    startContainer: range.startContainer,
                    endContainer: range.endContainer,
                    startOffset: range.startOffset,
                    endOffset: range.endOffset,
                    commonAncestorContainer: range.commonAncestorContainer,
                    parentElements: findRangeParentElements(range),
                });
                return true;
            };

            const pointerUpHandler = e => {
                if (e.target?.closest?.('mark')) return;
                setTimeout(() => {
                    try {
                        const sel = doc.defaultView?.getSelection();
                        if (!sel?.toString().trim()) {
                            this.onClearSelection();
                            return;
                        }
                        emitCurrentSelection();
                    } catch {}
                }, POINTERUP_SETTLE_MS);
            };
            doc.addEventListener('pointerup', pointerUpHandler);
            cleanups.push(() => doc.removeEventListener('pointerup', pointerUpHandler));

            // Catches selection clears that pointerup misses (click outside the
            // iframe, Escape, programmatic clear).
            let clearSelectionTimer = null;
            const selectionChangeHandler = () => {
                clearTimeout(clearSelectionTimer);
                clearSelectionTimer = setTimeout(() => {
                    try {
                        const sel = doc.defaultView?.getSelection();
                        if (sel?.toString().trim()) return;
                        // Caret inside a `<mark>` means the user just tapped a
                        // highlight; clearing now would close its popup mid-open.
                        if (isInsideMark(sel?.anchorNode)) return;
                        this.onClearSelection();
                    } catch {
                        // Cross-origin frames will throw; safe to ignore.
                    }
                }, SELECTIONCHANGE_CLEAR_MS);
            };
            doc.addEventListener('selectionchange', selectionChangeHandler);
            cleanups.push(() => {
                clearTimeout(clearSelectionTimer);
                doc.removeEventListener('selectionchange', selectionChangeHandler);
            });

            // Android long-press release doesn't fire `pointerup` (the system
            // selection UI captures the gesture). Use stabilized selectionchange
            // as the "selection finished" signal.
            if (onAndroid) {
                let androidEmitTimer = null;
                const androidSelectionEmit = () => {
                    clearTimeout(androidEmitTimer);
                    androidEmitTimer = setTimeout(() => {
                        try {
                            emitCurrentSelection();
                        } catch {}
                    }, ANDROID_SELECTION_SETTLE_MS);
                };
                doc.addEventListener('selectionchange', androidSelectionEmit);
                cleanups.push(() => {
                    clearTimeout(androidEmitTimer);
                    doc.removeEventListener('selectionchange', androidSelectionEmit);
                });
            }
        }

        this._selectionCleanups = cleanups;
    }

    /**
     * @R2 Check if the body has enough text content for legacy highlights to render.
     *
     * R2 fires frameLoaded with a placeholder body (~16 chars) before the real
     * decrypted content is available (in a separate iframe). This checks:
     * - For ALL highlights: body must have real content (> 50 chars), not a placeholder.
     * - For legacy highlights: body text must be long enough for the largest char offset.
     *
     * @param {Array} highlights - Highlights to check against
     * @returns {boolean} true if body content is sufficient for rendering
     */
    _isBodyContentReady(highlights) {
        const body = highlights[0]?.contextBodyDomNode;
        if (!body) return false;

        const bodyTextLength = body.textContent?.length || 0;

        // @R2 Placeholder detection: R2 placeholder bodies have minimal content (~16 chars).
        // Both R2 and legacy highlights need the real body to render.
        if (bodyTextLength <= 50) return false;

        // For legacy highlights, verify body has enough text for the largest offset
        const maxNeeded = this._getMaxNeededTextLength(highlights);
        if (maxNeeded > 0 && bodyTextLength < maxNeeded) return false;

        return true;
    }

    /**
     * @R2 Calculate the minimum body text length needed for legacy highlights.
     * @param {Array} highlights - All highlights (legacy will be filtered)
     * @returns {number} Max character offset + length needed across all legacy highlights
     */
    _getMaxNeededTextLength(highlights) {
        const legacyHighlights = highlights.filter(h => h.isLegacy?.());
        if (legacyHighlights.length === 0) return 0;
        return Math.max(
            ...legacyHighlights.map(
                h => (Number(h.epubLocationStartChar) || 0) + (h.totalText?.length || h.totalLength || 0)
            )
        );
    }

    /**
     * @R2 Find the content body that contains the highlight text.
     *
     * R2 may deliver onLoadFrame with a placeholder body while the real decrypted
     * content lives in a different iframe. This method searches for a body that
     * actually contains the highlight text (not just any body with enough chars,
     * which could be the table of contents or a different chapter).
     *
     * Search order:
     * 1. R2 navigator content frames (_cframes) — the visible rendering frames
     * 2. All iframes in #readiumViewport (flat scan)
     * 3. Nested iframes (iframes within iframes) — R2 may use deep nesting
     *
     * @param {Array} highlights - Highlights to validate body content against
     * @returns {HTMLElement|null} The body element containing highlight text, or null
     */
    _findContentBody(highlights) {
        const MIN_REAL_BODY_LENGTH = 100;
        const maxNeeded = Math.max(this._getMaxNeededTextLength(highlights), MIN_REAL_BODY_LENGTH);

        // @R2 Get a sample text snippet to validate we found the RIGHT chapter,
        // not just any iframe with enough characters (e.g. the table of contents).
        const sampleText = this._getSampleTextForValidation(highlights);

        const isValidBody = body => {
            const textLen = body?.textContent?.length || 0;
            if (textLen < maxNeeded) return false;
            // Validate body actually contains highlight text (not a different chapter)
            return this._bodyContainsSampleText(body, sampleText);
        };

        // 1. Primary: use R2's navigator content frames (the visible ones)
        if (this.getContentFrames) {
            const cframes = this.getContentFrames();
            for (const cframe of cframes) {
                try {
                    const body = readFrameDocument(cframe)?.body;
                    if (body && isValidBody(body)) {
                        return body;
                    }
                } catch {
                    // Cross-origin or unavailable frame
                }
            }
        }

        // 2. Scan all iframes in the viewport (flat + nested)
        const candidateBodies = this._collectAllIframeBodies();

        for (const { body } of candidateBodies) {
            if (isValidBody(body)) {
                return body;
            }
        }

        return null;
    }

    /**
     * @R2 Tells whether a chapter body holds a highlight's sample text. The raw text answers
     * almost always; the sample comes from `totalText`, whose whitespace runs are collapsed, so
     * on a miss the body is collapsed the same way before giving up: a `<pre>` or aligned verse
     * keeps its double spaces in the DOM. This runs inside a polling loop, so the cheap check
     * goes first.
     *
     * @param {HTMLElement} body - Candidate chapter body
     * @param {string|null} sampleText - Snippet from _getSampleTextForValidation
     * @returns {boolean}
     */
    _bodyContainsSampleText(body, sampleText) {
        if (!sampleText) return true;
        const bodyText = body.textContent;
        if (bodyText.includes(sampleText)) return true;
        return normalizeWhitespace(bodyText).replace(/\n/g, '').includes(sampleText);
    }

    /**
     * @R2 Get a short text snippet from highlights to validate the correct chapter body.
     * Uses the shortest highlight text (fastest to search) and picks a middle fragment
     * to avoid whitespace issues at boundaries.
     *
     * @param {Array} highlights - Highlights to extract sample from
     * @returns {string|null} A text snippet to search for, or null
     */
    _getSampleTextForValidation(highlights) {
        let shortest = null;
        for (const h of highlights) {
            const text = h.totalText?.replace(/\n/g, '');
            if (text && text.length >= 8) {
                if (!shortest || text.length < shortest.length) {
                    shortest = text;
                }
            }
        }
        if (!shortest) return null;

        // Use a middle fragment (avoids edge whitespace issues)
        if (shortest.length > 20) {
            const start = Math.floor(shortest.length * 0.2);
            return shortest.substring(start, start + 30);
        }
        return shortest;
    }

    /**
     * @R2 Collect body elements from all accessible iframes, including nested ones.
     * R2 may use deeply nested iframes for rendering decrypted EPUB content.
     *
     * @returns {Array<{body: HTMLElement, source: string, depth: number}>}
     */
    _collectAllIframeBodies() {
        const results = [];

        const scanIframes = (root, depth, sourcePath) => {
            let iframes;
            try {
                iframes = root.querySelectorAll('iframe');
            } catch {
                return;
            }
            for (const iframe of iframes) {
                try {
                    const body = iframe.contentDocument?.body;
                    if (body) {
                        const src = iframe.src?.substring(0, 60) || iframe.id || '(anon)';
                        results.push({ body, source: `${sourcePath}>${src}`, depth });
                        // Recurse into nested iframes
                        if (depth < 4) {
                            scanIframes(iframe.contentDocument, depth + 1, `${sourcePath}>${src}`);
                        }
                    }
                } catch {
                    // Cross-origin iframe, skip
                }
            }
        };

        // Start from the viewport element
        const viewport = document.getElementById('readiumViewport');
        if (viewport) {
            scanIframes(viewport, 0, '#readiumViewport');
        }

        // Also scan from document root (in case content iframes are outside the viewport)
        scanIframes(document, 0, 'document');

        // Deduplicate (same body from different paths)
        const seen = new Set();
        return results.filter(r => {
            if (seen.has(r.body)) return false;
            seen.add(r.body);
            return true;
        });
    }

    renderHighlight(highlight) {
        if (highlight.isRendered) {
            const renderedHighlightMarks = highlight.contextBodyDomNode.querySelectorAll(
                `.${highlight.getUniqueCssClass()}`
            );
            if (renderedHighlightMarks.length === 0) return;
            for (const mark of renderedHighlightMarks) {
                this.applyColorToMarker(mark, highlight.color);
            }
        } else {
            // A repaint must clear this highlight's previous marks first, over the whole body:
            // a text-search fallback paints every occurrence, and a leftover <mark> splits the
            // text nodes the selector path resolves and counts over. Unique class only: Mark.js
            // uses className as a CSS selector, so a multi-class string never unmarks.
            new Mark(highlight.contextBodyDomNode).unmark({ className: highlight.getUniqueCssClass() });

            if (highlight.isLegacy()) {
                // @R2 Legacy highlights: use text search (most robust for R1 → R2 rendering).
                // Text search finds the actual text in the DOM regardless of character offset
                // counting differences between R1 and R2, or DOM structure variations.
                // @R2 Lazy migration: compute R2 location BEFORE rendering
                // (Mark.js inserts <mark> tags that alter text node structure)
                let r2Location = null;
                if (!highlight.epubR2Location && highlight.totalText && highlight.persistR2Migration) {
                    r2Location = this.convertLegacyToR2(highlight);
                }

                const textSuccess = this.renderHighlightByText(highlight);

                if (textSuccess) {
                    // @R2 Lazy migration: persist full R2 location to DB only if conversion succeeded.
                    // If convertLegacyToR2 failed, keep as R1 so it retries on next load.
                    if (
                        !highlight.epubR2Location &&
                        highlight.totalText &&
                        highlight.persistR2Migration &&
                        r2Location
                    ) {
                        highlight.persistR2Migration(r2Location);
                    }
                } else {
                    // Text search failed — try Mark.js markRanges with char offsets as last resort
                    const startCharOffset = Number(highlight.epubLocationStartChar) || 0;
                    this.renderHighlightInPage(
                        highlight.contextBodyDomNode,
                        startCharOffset,
                        highlight.totalLength,
                        highlight.getCssClassForMarker(),
                        highlight.color,
                        highlight
                    );
                }
            } else {
                // @R2 path: try CSS selector rendering first (works for native R2 highlights)
                const r2Success = this.renderHighlightR2(highlight);

                if (!r2Success) {
                    // @R2 CSS selectors failed → fall back to text search
                    this.renderHighlightByText(highlight);
                }
            }
        }
    }

    /**
     * @R2 Render a highlight using the R2 CSS selector-based path.
     * Extracted from renderHighlight() for reuse by both native R2 and migrated R1 highlights.
     * @returns {boolean} true if rendering succeeded, false if selectors couldn't be resolved
     */
    renderHighlightR2(highlight) {
        if (!highlight.location?.selection?.startCss || !highlight.location?.selection?.endCss) {
            return false;
        }

        const body = highlight.contextBodyDomNode;
        const doc = body.ownerDocument;
        const range = doc.createRange();

        function findFirstTextNode(el) {
            const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT, node => {
                return node.textContent.length > 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
            });
            return walker.nextNode();
        }

        const { startCss, endCss, startOffset, endOffset, startElementOffset } = highlight.location.selection;

        const startElement = body.querySelector(startCss);
        if (!startElement) {
            return false;
        }
        const startNode = findFirstTextNode(startElement) || startElement;

        const endElement = body.querySelector(endCss);
        if (!endElement) {
            return false;
        }
        const endNode = findFirstTextNode(endElement) || endElement;

        try {
            range.setStart(startNode, startOffset);
            range.setEnd(endNode, endOffset);
        } catch {
            return false;
        }

        // Mark.js counts characters over its contexts in the order it receives them, so the
        // context must be a single element that encloses the range: one DOM-ordered walk. A
        // list that puts the start/end leaves before their own block (fixed-layout EPUBs wrap
        // every word in an absolutely positioned <span>) shifts the count into the wrong words.
        // The context may never sit below the start element: `startElementOffset` is measured
        // from that element's start, and a range rebuilt inside a nested inline would otherwise
        // count from the inline instead.
        const enclosingElement = findEnclosingElement(range.commonAncestorContainer);
        const markContext = startElement.contains(enclosingElement) ? startElement : enclosingElement;
        const markStartOffset = measureMarkStartOffset(markContext, startElement, startElementOffset, range);
        const markLength = measureMarkLength(markContext, markStartOffset, range);

        try {
            // Treat a zero-mark result as failure so renderHighlight falls back to the
            // text-search path (which has its own newline→space retry).
            const markCount = this.renderHighlightInPage(
                markContext,
                markStartOffset,
                markLength,
                highlight.getCssClassForMarker(),
                highlight.color,
                highlight
            );
            return markCount > 0;
        } catch (e) {
            Sentry.captureException(e, {
                level: 'warning',
                tags: { feature: 'highlights' },
                extra: {
                    method: 'renderHighlightInPage',
                    highlightId: highlight.id,
                    highlightText: highlight.totalText,
                    markStartOffset,
                    totalLength: highlight.totalLength,
                },
            });
            return false;
        }
    }

    /**
     * @R2 Render a highlight by searching for its text content in the DOM.
     *
     * More robust than CSS selector-based rendering — finds the text regardless
     * of DOM structure or iframe source. Uses Mark.js mark() with acrossElements
     * to handle text spanning multiple HTML elements (paragraphs, spans, etc.).
     *
     * @param {Object} highlight - Highlight with totalText and contextBodyDomNode
     * @returns {boolean} true if at least one mark was created
     */
    renderHighlightByText(highlight) {
        const body = highlight.contextBodyDomNode;
        if (!body || !highlight.totalText) {
            return false;
        }

        const searchText = highlight.totalText.replace(/\n/g, '');

        if (searchText.length < 2) {
            return false;
        }

        const className = highlight.getCssClassForMarker();
        const Marker = new Mark(body);

        let markCount = 0;

        const markOptions = {
            acrossElements: true,
            separateWordSearch: false,
            className,
            each: mark => {
                markCount++;
                mark.addEventListener('click', e => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.onSelectHighlight(highlight);
                });
                mark.addEventListener('tap', e => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.onSelectHighlight(highlight);
                });
                this.applyColorToMarker(mark, highlight.color);
                highlight.isRendered = true;
            },
            noMatch: () => {},
            done: () => {},
        };

        Marker.mark(searchText, markOptions);

        // R2 stores \n for element boundaries. In the DOM, that boundary may be
        // actual whitespace (e.g. <span>9</span> Text). Retry with spaces if
        // the stripped version didn't match.
        if (markCount === 0 && highlight.totalText.includes('\n')) {
            const searchTextWithSpaces = highlight.totalText.replace(/\n/g, ' ').trim();
            if (searchTextWithSpaces !== searchText) {
                Marker.mark(searchTextWithSpaces, markOptions);
            }
        }

        return markCount > 0;
    }

    /**
     * @R2 Lazy migration: Convert an R1 legacy highlight to R2 format.
     *
     * Walks text nodes in the body using the body-level character offset (epubLocationStartChar)
     * to locate the start and end positions, then generates CSS selectors for the parent elements.
     * Also extracts navigation data (href, progression) for R2 Locator-based navigation.
     *
     * @param {Object} highlight - Legacy highlight with epubLocationStartChar and totalText
     * @returns {Object|null} EpubR2Location with selection and navigation data, or null if failed
     */
    convertLegacyToR2(highlight) {
        try {
            const body = highlight.contextBodyDomNode;
            const doc = body.ownerDocument;
            const startCharOffset = Number(highlight.epubLocationStartChar) || 0;

            // R1 replaces \n with __ for length (2 chars per newline instead of 1).
            // This matches how Mark.js counts characters in the DOM.
            const adjustedLength = highlight.totalText.split('\n').join('__').length;
            const endCharOffset = startCharOffset + adjustedLength;

            // Walk text nodes sequentially (same counting as Mark.js markRanges)
            const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT, null);
            let currentOffset = 0;
            let node;
            let startNode = null,
                startNodeOffset = 0,
                startParent = null;
            let endNode = null,
                endNodeOffset = 0,
                endParent = null;

            while ((node = walker.nextNode())) {
                const nodeLength = node.textContent.length;

                if (!startNode && currentOffset + nodeLength > startCharOffset) {
                    startNode = node;
                    startNodeOffset = startCharOffset - currentOffset;
                    startParent = node.parentElement;
                }

                if (currentOffset + nodeLength >= endCharOffset) {
                    endNode = node;
                    endNodeOffset = endCharOffset - currentOffset;
                    endParent = node.parentElement;
                    break;
                }

                currentOffset += nodeLength;
            }

            if (!startNode || !endNode || !startParent || !endParent) {
                return null;
            }

            // Generate CSS selectors relative to body
            const selectorOptions = {
                root: body,
                combineBetweenSelectors: false,
                selectors: ['id', 'tag', 'class', 'attribute', 'nthchild', 'nthoftype'],
            };

            const startCss = getCssSelector(startParent, selectorOptions);
            const endCss = getCssSelector(endParent, selectorOptions);

            if (!startCss || !endCss) {
                return null;
            }

            const startElementOffset = countTextBefore(startParent, startNode, startNodeOffset);

            // Extract href from the iframe's <base> tag (injected by R2)
            // Use getRelativeHref to convert full URL to publication-relative path
            const baseEl = doc.querySelector('base[data-readium]');
            const fullHref = baseEl?.getAttribute('href') || '';
            const href = this.getRelativeHref(fullHref) || '';

            // Calculate progression within the chapter (0-1 range)
            const bodyTextLength = body.textContent?.length || 1;
            const progression = Math.min(1, Math.max(0, startCharOffset / bodyTextLength));

            return {
                // Selection data (for rendering)
                startCss,
                endCss,
                startElementOffset,
                startOffset: startNodeOffset,
                endOffset: endNodeOffset,
                text: highlight.totalText,
                // Navigation data (for R2 Locator)
                href,
                type: 'application/xhtml+xml',
                locations: {
                    progression,
                },
            };
        } catch (error) {
            console.warn('@R2 Lazy migration failed for highlight', highlight.uuid, error);
            return null;
        }
    }

    /**
     * Marks a run of text inside `context` by character offset (Mark.js markRanges).
     * R2 highlights call it with the element enclosing their resolved Range; the legacy
     * fallback calls it with the chapter body and R1's epubLocationStartChar, which is less
     * reliable because character counting can differ between the R1 and R2 DOM structures.
     *
     * @param {HTMLElement} context - Element whose text Mark.js counts over
     * @param {number} startOffset - Character offset from the start of `context`
     * @param {number} length - Length of highlighted text
     * @param {string} className - CSS class for the mark elements
     * @param {string} color - Highlight color
     * @param {Object} highlight - The highlight model object
     */
    renderHighlightInPage(context, startOffset, length, className, color, highlight) {
        if (!context) {
            return 0;
        }

        // Legacy R1 expands each newline to two chars (Mark.js counts them); R2 callers pass the
        // real rendered length (range.toString().length), so newlines must NOT be stripped there.
        const computedLength = highlight.isLegacy() ? highlight.totalText.split('\n').join('__').length : length;

        const Marker = new Mark(context);

        let markCount = 0;
        Marker.markRanges(
            [
                {
                    start: startOffset,
                    length: computedLength,
                },
            ],
            {
                className,
                each: mark => {
                    markCount++;
                    mark.addEventListener('click', e => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.onSelectHighlight(highlight);
                    });
                    mark.addEventListener('tap', e => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.onSelectHighlight(highlight);
                    });
                    this.applyColorToMarker(mark, color);
                    highlight.isRendered = true;
                },
                noMatch: () => {},
                done: () => {},
            }
        );

        return markCount;
    }

    renderHighlightInPageWhenIsSearching(context, queryWords, frameIsLoading) {
        clearTimeout(this.markerSearch);

        if (frameIsLoading) {
            this.markerSearch = setTimeout(() => {
                this.renderHighlightInPageWhenIsSearching(context, queryWords, frameIsLoading);
            }, 1000);
            return;
        }

        const className = { className: 'search' };
        const color = '#0fff13';

        context.map(contextItem => {
            const Marker = new Mark(contextItem.bodyDomNode);
            Marker.unmark(className);
            Marker.mark(queryWords, {
                ...className,
                accuracy: 'partially',
                each: mark => {
                    mark.style.setProperty('background-color', hexToRgba(color, 0.3), 'important');
                },
            });
        });

        this.onFulfillQueryWordsHighlight();
    }

    applyColorToMarker(mark, color) {
        // Marks tagged `marker-underline` (e.g. flashcards) render as a colored underline
        // instead of a background fill. The fill style is inline-!important, so the variant
        // must be applied here rather than via CSS.
        if (mark.classList.contains('marker-underline')) {
            mark.style.setProperty('background-color', 'transparent', 'important');
            mark.style.setProperty('text-decoration-line', 'underline', 'important');
            mark.style.setProperty('text-decoration-color', color, 'important');
            mark.style.setProperty('text-decoration-thickness', '2px', 'important');
        } else {
            mark.style.setProperty('background-color', hexToRgba(color, 0.3), 'important');
        }
        mark.style.cursor = 'pointer';
    }

    /**
     * Removes the Mark.js marks for a class across the given chapter bodies, with no
     * deletion breadcrumb/callback. Use to repaint or clear markers (e.g. flashcards on
     * create/update/delete) where a `highlight_removed` event would be misleading.
     * @param {string} className - The marker class to unmark
     * @param {Array} context - Chapter context items with bodyDomNode
     */
    unmarkClassName(className, context) {
        context.map(contextItem => {
            const Marker = new Mark(contextItem.bodyDomNode);
            Marker.unmark({ className });
        });
    }

    /**
     * Removes a highlight's marks from the DOM and reports the deletion. Unmarks the accumulated
     * Vuex context bodies (reflowable) and also the body the highlight was actually rendered into
     * (fixed layout, where the context array can point to stale spread bodies).
     * @param {string} className - The marker class to unmark (`uuid-<uuid>`)
     * @param {Array} context - Chapter context items with bodyDomNode
     */
    removeHighlight(className, context) {
        sendBreadcrumb('selection', 'highlight_removed', {
            chapterHref: this.currentChapterHref,
        });
        this.unmarkClassName(className, context);
        this._dropTrackedItem(className.replace(/^uuid-/, ''));
        this.onMarkersRendered();
        this.onHighlightDeleted();
    }

    /**
     * Removes a flashcard's marks from the DOM and drops it from the current-chapter map.
     * @param {string} uuid - The flashcard's uuid
     */
    removeFlashcard(uuid) {
        this._dropTrackedItem(uuid);
    }

    /**
     * Unmarks a tracked item's marks from the body it was rendered into and drops it from the
     * current-chapter map, so a later cframe re-render won't repaint a stale item.
     * @param {string} uuid - The highlight/flashcard uuid
     */
    _dropTrackedItem(uuid) {
        const trackedBody = this.currentChapterHighlights[uuid]?.contextBodyDomNode;
        if (trackedBody) {
            new Mark(trackedBody).unmark({ className: `uuid-${uuid}` });
        }
        delete this.currentChapterHighlights[uuid];
    }

    /**
     * Renders a single flashcard into the current chapter — only if its content is present in a
     * live frame (so flashcards belonging to other chapters are skipped) — and tracks it so cframe
     * re-renders keep it. Used for a live flashcard create.
     * @param {Object} flashcard - A render-contract flashcard model instance
     * @returns {boolean} true if it was rendered in the current chapter
     */
    renderFlashcard(flashcard) {
        // No anchor to place the marker (e.g. a flashcard created from the panel with no source
        // selection): nothing to paint. Without this guard _findContentBody would match an
        // arbitrary body and track an unpaintable flashcard that rides every cframe re-render.
        if (!flashcard.location?.selection && !flashcard.totalText) return false;
        // Prefer the live body inherited from the selection (the visible frame the user
        // selected in). Only re-discover when that body is no longer in the document
        // (navigation / R2 re-layout) — re-discovery can land on an offscreen prefetch frame.
        const body = this._isHighlightInLiveFrame(flashcard)
            ? flashcard.contextBodyDomNode
            : this._findContentBody([flashcard]);
        if (!body) return false;
        flashcard.isRendered = false;
        flashcard.contextBodyDomNode = body;
        this.currentChapterHighlights[flashcard.uuid] = flashcard;
        this.renderHighlight(flashcard);
        this._watchForCframeRerender();
        return true;
    }

    /**
     * Repaints a flashcard's marker: unmark its old paint, then render it again. Used for a live
     * recolor (the marker class is uuid-based, so unmark-then-render swaps the color).
     * @param {Object} flashcard - A render-contract flashcard model instance
     * @returns {boolean} true if it was rendered in the current chapter
     */
    repaintFlashcard(flashcard) {
        this.removeFlashcard(flashcard.uuid);
        return this.renderFlashcard(flashcard);
    }

    /**
     * Collects the note-marker anchors of the current chapter's annotated
     * highlights, in the parent window's viewport coordinates. Delegates the
     * geometry to `collectEpubNoteMarkerAnchors`; this manager only owns the
     * highlight state it feeds in.
     * @returns {Array<{uuid: string, color: string, top: number, columnTextRight: number, columnBoundRight: number}>}
     */
    collectNoteMarkerAnchors() {
        return collectEpubNoteMarkerAnchors(this.currentChapterHighlights);
    }

    /**
     * Whether a highlight has a body node still attached to a live document. EPUB
     * content iframes come and go per chapter, so a highlight for an unloaded or
     * torn-down chapter has no paintable node.
     * @param {Object} highlight - Highlight model instance
     * @returns {boolean}
     */
    _isHighlightInLiveFrame(highlight) {
        const body = highlight?.contextBodyDomNode;
        return !!body && body.isConnected;
    }

    /**
     * Syncs a saved highlight's color and annotation state into the
     * current-chapter state and repaints it, but only when there is a live frame
     * to paint into — otherwise the values are kept and initHighlights repaints
     * it when the chapter loads.
     * @param {Object} selection - The highlight being saved (carries the new color)
     */
    _syncSavedHighlightRender(selection) {
        const trackedHighlight = this.currentChapterHighlights[selection.uuid];
        if (trackedHighlight) {
            trackedHighlight.color = selection.color;
            trackedHighlight.colorSlug = selection.colorSlug;

            if (selection.hasAnnotation) {
                trackedHighlight.hasAnnotation = true;
                trackedHighlight.annotationText = selection.annotationText;
                trackedHighlight.annotationHtml = selection.annotationHtml;
            }
        }

        // A live selection is one just created in the visible chapter: render it,
        // and if it isn't tracked yet, add it to this chapter's highlights.
        if (this._isHighlightInLiveFrame(selection)) {
            this.renderHighlight(selection);
            if (!trackedHighlight) {
                this.currentChapterHighlights[selection.uuid] = selection;
            }
        } else if (this._isHighlightInLiveFrame(trackedHighlight)) {
            // Recoloring from the notes panel: `selection` is a bare copy with no
            // link to the rendered DOM, so repaint trackedHighlight instead — the
            // same highlight already on screen in this chapter, which holds that link.
            this.renderHighlight(trackedHighlight);
        }
    }

    async saveHighlight(params, selection, anonymousSession) {
        const newColor = params.color;
        const newColorSlug = params.colorSlug;
        const oldColor = selection.color;
        const oldColorSlug = selection.colorSlug;
        selection.color = newColor;
        selection.colorSlug = newColorSlug;

        sendBreadcrumb('selection', 'highlight_saved', {
            chapterHref: this.currentChapterHref,
            color: newColor,
            hasNote: !!(params.hasAnnotation && params.annotationText?.length),
        });

        let needUpdatedAnnotationText = false;
        if (params.hasAnnotation && params.annotationText.length !== 0) {
            selection.hasAnnotation = params.hasAnnotation;
            selection.annotationText = params.annotationText;
            selection.annotationFormat = params.annotationFormat ?? null;
            // A plain-text edit is the whole annotation from now on: keeping the
            // stored markup would make the next save re-send it over the edit.
            if (selection.annotationFormat !== 'html') selection.annotationHtml = null;
            needUpdatedAnnotationText = params.needUpdated;
        }

        if (anonymousSession) {
            this._syncSavedHighlightRender(selection);
            this.onMarkersRendered();
            this.onHandleAnonymousSessionSelections(selection, oldColor, newColor);
            this.onTrackEvent({
                action: 'create annon highlight',
                trigger: '',
                source: 'readium',
            });
            return;
        }

        if (!selection.confirmed) {
            await selection.store();
            this.onTrackEvent({
                action: 'create highlight',
                trigger: '',
                source: 'readium',
            });
        } else if (newColorSlug !== oldColorSlug || needUpdatedAnnotationText) {
            await selection.update();
            await selection.updateAnnotationText();
            this.onHighlightUpdated();
        }

        this._syncSavedHighlightRender(selection);
        this.onMarkersRendered();
        this.onHighlightCreated(selection);
    }

    getCurrentChapterHighlights() {
        return this.currentChapterHighlights;
    }

    setCurrentChapterHighlights(highlights) {
        this.currentChapterHighlights = highlights;
    }

    resetInitHighlightsRetryCount() {
        this.initHighlightsRetryCount = 0;
    }

    clearMarkerSearchTimeout() {
        if (this.markerSearch) {
            clearTimeout(this.markerSearch);
            this.markerSearch = null;
        }
    }

    /**
     * Cleans up all timers, event listeners, and observer references.
     */
    destroy() {
        const hadCframeWatcher = !!this._cframeWatcherId;
        const selectionCleanupCount = this._selectionCleanups?.length ?? 0;
        const hadResizeObserver = !!this.epubNavigator?.resizeObserver;
        const hadFadeAnimator = !!this._fadeAnimator;

        if (this._cframeWatcherId) {
            clearInterval(this._cframeWatcherId);
            this._cframeWatcherId = null;
        }

        this.clearMarkerSearchTimeout();

        if (this._selectionCleanups) {
            this._selectionCleanups.forEach(fn => fn());
            this._selectionCleanups = null;
        }

        if (this.epubNavigator?.resizeObserver) {
            this.epubNavigator.resizeObserver.disconnect();
        }

        if (this._fadeAnimator) {
            this._fadeAnimator.cancel();
            this._fadeAnimator = null;
        }

        this.epubNavigator = null;
        this.publication = null;

        sendBreadcrumb('lifecycle', 'highlight_manager_destroyed', {
            hadCframeWatcher,
            selectionCleanupCount,
            hadResizeObserver,
            hadFadeAnimator,
        });
    }
}
